// =====================================================================
//  Cotización de agregadores directa desde el navegador (sin créditos Base44)
//  Envia/Tendencys: API con CORS ✓ — cotiza DHL, FedEx, UPS, Estafeta,
//  Paquetexpress, Redpack y 20+ carriers directamente desde el cliente.
//  Autor: JORGE ALBERTO BAEZ PEREZ
//  Huella SHA-256: 43f31a1049af791677048c8a2bb0f5d19c081b4a267bf51a27e6c25b270cc69d
// =====================================================================

import { base44 } from "@/api/base44Client";
import { CARRIERS } from "@/lib/carriers";
import { quoteSrSimiClient, quoteElogicYaClient } from "@/lib/clientAggregatorsCors";
import { quoteViaWorker } from "@/lib/quoteWorkerConfig";

// Country name → ISO 3166-1 alpha-2
function countryAlpha2(name) {
  if (!name) return "MX";
  const s = String(name).trim().toLowerCase();
  if (s.length === 2) return s.toUpperCase();
  const MAP = {
    mexico: "MX", "méxico": "MX",
    "estados unidos": "US", usa: "US", eeuu: "US",
    canada: "CA", espana: "ES", "españa": "ES",
    colombia: "CO", argentina: "AR", brasil: "BR", brazil: "BR",
    peru: "PE", chile: "CL", guatemala: "GT", cuba: "CU"
  };
  return MAP[s] || "MX";
}

// Mapa CP prefijo (primeros 2 dígitos) → código de estado (formato Envia).
// Construido desde la API de geocoding de Envia para los 100 prefijos.
// Reemplaza la llamada a geocodes.envia.com (que no soporta CORS) cuando
// se cotiza desde el navegador.
const CP_PREFIX_STATE = {
  "00": "CX","01": "CX","02": "CX","03": "CX","04": "CX","05": "CX","06": "CX","07": "CX","08": "CX","09": "CX",
  "10": "CX","11": "CX","12": "CX","13": "CX","14": "CX","15": "CX","16": "CX",
  "17": "EM","18": "EM","19": "EM",
  "20": "AG","21": "BC","22": "BC","23": "BS","24": "CM",
  "25": "CO","26": "CO","27": "CO","28": "CL","29": "CS","30": "CS",
  "31": "CH","32": "CH","33": "CH","34": "DG","35": "DG",
  "36": "GT","37": "GT","38": "GT","39": "GR","40": "GR","41": "GR",
  "42": "HG","43": "HG",
  "44": "JA","45": "JA","46": "JA","47": "JA","48": "JA","49": "JA",
  "50": "EM","51": "EM","52": "EM","53": "EM","54": "EM","55": "EM","56": "EM","57": "EM",
  "58": "MI","59": "MI","60": "MI","61": "MI",
  "62": "MO","63": "NA",
  "64": "NL","65": "NL","66": "NL","67": "NL",
  "68": "OA","69": "OA","70": "OA","71": "OA",
  "72": "PU","73": "PU","74": "PU","75": "PU",
  "76": "QT","77": "QR","78": "SL","79": "SL",
  "80": "SI","81": "SI","82": "SI",
  "83": "SO","84": "SO","85": "SO",
  "86": "TB","87": "TM","88": "TM","89": "TM",
  "90": "TL",
  "91": "VE","92": "VE","93": "VE","94": "VE","95": "VE","96": "VE",
  "97": "YU","98": "ZA","99": "ZA"
};

function cpToState(cp) {
  const p = String(cp || "").trim();
  if (p.length < 2) return "CX";
  return CP_PREFIX_STATE[p.slice(0, 2)] || "CX";
}

function enviaPkgType(pt) {
  if (pt === "sobre") return "envelope";
  if (pt === "tarima" || pt === "camion") return "pallet";
  return "box";
}

function etaFromDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + (Number(days) || 1));
  return d.toISOString().slice(0, 10);
}

function parseDaysEnvia(v) {
  const m = String(v || "").match(/\d+/);
  return m ? parseInt(m[0], 10) : 0;
}

// Parsea cargos adicionales de la respuesta de Envia (combustible, zona extendida, etc.)
function parseAdditionalCharges(item) {
  const list = Array.isArray(item?.costAdditionalCharges) ? item.costAdditionalCharges
    : Array.isArray(item?.additional_charges) ? item.additional_charges
    : Array.isArray(item?.breakdown) ? item.breakdown
    : Array.isArray(item?.surcharges) ? item.surcharges : [];
  let fuel = 0, special = 0, extended = 0, plus = 0, other = 0;
  for (const ch of list) {
    const amt = Number(ch.cost || ch.commission || ch.amount || ch.value || 0);
    if (amt <= 0) continue;
    const type = String(ch.additionalService || ch.type || ch.code || ch.name || "").toLowerCase();
    if (type === "fuel" || type.includes("combustible")) fuel += amt;
    else if (type.includes("special") || type.includes("handling") || type.includes("manejo")) special += amt;
    else if (type.includes("extended") || type.includes("extendida") || type.includes("remote") || type.includes("area")) extended += amt;
    else if (type.includes("plus")) plus += amt;
    else other += amt;
  }
  const additionalTotal = Number(item?.additionalCharges) || Number(item?.additional_charges) || (fuel + special + extended + plus + other);
  return { fuel, special, extended, plus, other, additionalTotal };
}

// Aplica margen de plataforma sobre la tarifa del agregador (precio de venta =
// costo del proveedor + margen). Sin config, el margen es 0 (tarifa directa).
function applyAggregatorMarkup(rate, rawCfg) {
  const providerCost = Number(rate.total_cost) || 0;
  // Piso garantizado: sin config o margen none/0 → estándar 16% + $5.
  const cfg = (rawCfg && rawCfg.markup_type !== "none" && ((Number(rawCfg.markup_value) || 0) > 0 || (Number(rawCfg.markup_fixed) || 0) > 0))
    ? rawCfg : { markup_type: "both", markup_value: 16, markup_fixed: 5 };
  const type = cfg.markup_type || "percent";
  const value = Number(cfg.markup_value) || 0;
  const fixed = Number(cfg.markup_fixed) || 0;
  let margin = 0;
  if (type === "none") margin = 0;
  else if (type === "fixed") margin = value;
  else if (type === "both") margin = fixed + providerCost * (value / 100);
  else margin = providerCost * (value / 100);
  const sellingPrice = Number((providerCost + margin).toFixed(2));
  return { ...rate, provider_cost: Number(providerCost.toFixed(2)), platform_margin: Number(margin.toFixed(2)), total_cost: sellingPrice };
}

// Margen estándar (16% + $5) adicional sobre los cargos de zona extendida/plus,
// igual que el backend (applyExtendedZoneMargin): el 100% del cargo va al extremo
// atribuido (destino/EDA por defecto en e-commerce) y se suma a platform_margin.
function applyZoneMarginClient(rate, rawCfg) {
  const ext = Number(rate.extended_zone_charge || 0) + Number(rate.plus_zone_charge || 0);
  if (ext <= 0) return rate;
  const zoneMargin = (rawCfg && rawCfg.markup_type !== "none" && ((Number(rawCfg.markup_value) || 0) > 0 || (Number(rawCfg.markup_fixed) || 0) > 0))
    ? rawCfg : { markup_type: "both", markup_value: 16, markup_fixed: 5 };
  const value = Number(zoneMargin.markup_value) || 0;
  const fixed = Number(zoneMargin.markup_fixed) || 0;
  const type = zoneMargin.markup_type || "percent";
  const m = type === "fixed" ? value : type === "both" ? fixed + ext * (value / 100) : ext * (value / 100);
  return {
    ...rate,
    eda_margin: Number(m.toFixed(2)),
    platform_margin: Number(((Number(rate.platform_margin) || 0) + m).toFixed(2)),
    total_cost: Number((Number(rate.total_cost) + m).toFixed(2))
  };
}

const ENVIA_BASE = {
  test: "https://api-test.envia.com",
  production: "https://api.envia.com"
};

// Caché de configuración: lee AggregatorPublicConfig (entidad pública) una vez
// y la reutiliza por 30s. Evita una lectura de entidad en cada cotización.
let _configCache = null;
let _configTs = 0;
async function loadConfig() {
  const now = Date.now();
  if (_configCache && now - _configTs < 30000) return _configCache;
  try {
    const page = await base44.entities.AggregatorPublicConfig.filter(
      { active: true },
      { limit: 1 }
    );
    _configCache = (page.items || [])[0] || null;
  } catch { _configCache = null; }
  _configTs = now;
  return _configCache;
}

// Cotiza un envío vía Envia/Tendencys directamente desde el navegador.
// Procesa los carriers en lotes de 3 (limitación de rate de Envia).
// Entrada: { origin_cp, dest_cp, origin_country, dest_country, weight_kg, dims, package_type, config }
// Salida: [{ carrier, service, total_cost, transit_days, source: "envia", aggregator: "envia", ... }]
async function quoteEnviaClient(token, mode, payload, markupCfg) {
  if (!token) return [];
  const base = ENVIA_BASE[mode === "production" ? "production" : "test"];
  const weight = Math.max(Number(payload.weight_kg) || 1, 0.1);
  const oCountry = countryAlpha2(payload.origin_country || "México");
  const dCountry = countryAlpha2(payload.dest_country || "México");

  // Carriers a cotizar: los que soportan Envia (enviaCode) y aplican al tipo de envío.
  const carriers = CARRIERS
    .filter(c => (c.aggregators || []).includes("envia") && c.enviaCode)
    .map(c => ({ id: c.id, enviaCode: c.enviaCode }));

  if (!carriers.length) return [];

  const baseBody = {
    origin: {
      name: "BHGC Nexus", phone: "+525500000000", street: "S/N", number: "S/N",
      district: "Centro", city: "Ciudad", state: oCountry === "MX" ? cpToState(payload.origin_cp) : "",
      country: oCountry, postalCode: String(payload.origin_cp || "")
    },
    destination: {
      name: "Destinatario", phone: "+525500000000", street: "S/N", number: "S/N",
      district: "Centro", city: "Ciudad", state: dCountry === "MX" ? cpToState(payload.dest_cp) : "",
      country: dCountry, postalCode: String(payload.dest_cp || "")
    },
    packages: [{
      type: enviaPkgType(payload.package_type),
      content: "Mercancía", amount: 1, declaredValue: 500,
      weight: Number(weight.toFixed(2)), lengthUnit: "CM", weightUnit: "KG",
      dimensions: {
        length: Math.max(Number(payload.length_cm) || 1, 1),
        width: Math.max(Number(payload.width_cm) || 1, 1),
        height: Math.max(Number(payload.height_cm) || 1, 1)
      }
    }]
  };

  const results = [];
  const BATCH = 3;
  const quoteOne = async (c) => {
    try {
      const res = await fetch(`${base}/ship/rate/`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ ...baseBody, shipment: { type: 1, carrier: c.enviaCode } })
      });
      const raw = await res.text();
      let data;
      try { data = JSON.parse(raw); } catch { data = {}; }
      if (!res.ok || data?.meta === "error") return;
      const list = Array.isArray(data?.data) ? data.data : [];
      for (const item of list) {
        const total = Number(item.totalPrice || item.total || item.price || 0);
        if (!total) continue;
        const days = (item.deliveryDate && item.deliveryDate.dateDifference ? Number(item.deliveryDate.dateDifference) : parseDaysEnvia(item.deliveryEstimate || item.deliveryTime || item.eta)) || 1;
        const ch = parseAdditionalCharges(item);
        const baseCost = Math.max(0, total - ch.additionalTotal);
        const rate = {
          carrier: c.id,
          service: item.serviceDescription || item.service || "Nacional",
          base_cost: Number(baseCost.toFixed(2)),
          fuel_surcharge: Number(ch.fuel.toFixed(2)),
          special_handling: Number(ch.special.toFixed(2)),
          extended_zone_charge: Number(ch.extended.toFixed(2)),
          plus_zone_charge: Number(ch.plus.toFixed(2)),
          other_charges: Number(ch.other.toFixed(2)),
          total_cost: Number(total.toFixed(2)),
          transit_days: days,
          source: "envia",
          aggregator: "envia",
          enviaService: item.service || undefined
        };
        results.push(applyZoneMarginClient(applyAggregatorMarkup(rate, markupCfg), markupCfg));
      }
    } catch (e) {
      // Error de red/CORS en un carrier: no rompe los demás
    }
  };
  for (let i = 0; i < carriers.length; i += BATCH) {
    await Promise.all(carriers.slice(i, i + BATCH).map(quoteOne));
  }

  // Dedupe: mismo carrier + servicio + source + aggregator + precio
  const seen = new Set();
  return results.filter(r => {
    const k = `${r.carrier}|${r.service || ""}|${r.source}|${r.aggregator || ""}|${r.total_cost}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  }).map(r => (r.eta ? r : { ...r, eta: etaFromDays(r.transit_days) }));
}

// Punto de entrada: cotiza todos los agregadores que soportan CORS (actualmente
// solo Envia/Tendencys) directamente desde el navegador. Devuelve tarifas
// normalizadas listas para mostrar en el cotizador. No consume créditos Base44.
export async function clientQuoteAggregators(payload) {
  // Worker externo independiente (guarda los tokens en el servidor, funciona para todos los usuarios).
  const viaWorker = await quoteViaWorker(payload);
  if (viaWorker.length) return viaWorker;
  const config = await loadConfig();
  if (!config) return [];

  const markupCfg = {
    markup_type: config.envia_markup_type || "both",
    markup_value: config.envia_markup_value ?? 16,
    markup_fixed: config.envia_markup_fixed ?? 5
  };

  // Envia, SrSimi y ELogicYa (las tres APIs con CORS) en paralelo; un fallo no rompe a las demás.
  const results = await Promise.all([
    config.envia_token ? quoteEnviaClient(config.envia_token, config.envia_mode, payload, markupCfg).catch(() => []) : [],
    quoteSrSimiClient(config, payload).catch(() => []),
    quoteElogicYaClient(config, payload).catch(() => [])
  ]);
  return results.flat();
}